import { ApiService } from "../services/api";
import { Session } from "./session";
import { RemoteLogger } from "../services/remoteLogger";
import { getDeviceId } from "./deviceId";

/**
 * Authentication Service
 */

export interface LoginUser {
  id: number;
  username: string;
  firstName: string;
  lastName: string;
  avatar: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LoginResponseData {
  user: LoginUser;
  token: string;
}

export interface LoginResponse {
  success: boolean;
  data: LoginResponseData;
  message: string;
  version: string;
}

export class AuthService {
  static async login(email: string, password: string): Promise<{ success: boolean; error?: string }> {
    RemoteLogger.info("[AuthService] login called with:", { email, passwordLength: password ? password.length : 0 });
    console.log("%c[Auth] Login attempt for:", "color: #ffb74d; font-weight: bold", email);

    const response = await ApiService.post<LoginResponse>("/users/login", { username: email, password });

    console.log("%c[Auth] Full response:", "color: #ffb74d", response);

    // API wraps result in: { success, data: { user, token }, message }
    const payload = response.data?.data;
    if (payload && payload.token) {
      console.log("%c[Auth] ✅ Login successful! Token received.", "color: #81c784; font-weight: bold");
      console.log("[Auth] User:", payload.user);
      await Session.setToken(payload.token);
      await Session.setUser(payload.user);
      return { success: true };
    }

    console.warn("%c[Auth] ❌ Login failed:", "color: #e57373", response.error);
    return {
      success: false,
      error: response.error || "Invalid credentials"
    };
  }

  static async initiatePushLogin(phoneCountryCode: string, phone: string): Promise<{ success: boolean; challengeId?: string; error?: string }> {
    try {
      RemoteLogger.info("[AuthService] initiatePushLogin called with:", { phoneCountryCode, phone });
      const response = await ApiService.post<{ success: boolean; message?: string; data?: { challengeId: string } }>("/auth/push/initiate", {
        phoneCountryCode,
        phone,
        context: "WIDGET_LOGIN",
      });

      if (response.data && response.data.success && response.data.data?.challengeId) {
        return { success: true, challengeId: response.data.data.challengeId };
      }
      return { success: false, error: response.error || response.data?.message || "Failed to initiate push login." };
    } catch (err: any) {
      return { success: false, error: err?.message || "An error occurred." };
    }
  }

  static async initiateTruIdPushLogin(handle: string): Promise<{ success: boolean; challengeId?: string; error?: string }> {
    try {
      RemoteLogger.info("[AuthService] initiateTruIdPushLogin called with:", { handle });
      const response = await ApiService.post<{ success: boolean; message?: string; data?: { challengeId: string } }>("/auth/push/initiate", {
        handle,
        context: "WIDGET_LOGIN",
      });

      if (response.data && response.data.success && response.data.data?.challengeId) {
        return { success: true, challengeId: response.data.data.challengeId };
      }
      return { success: false, error: response.error || response.data?.message || "Failed to initiate push login." };
    } catch (err: any) {
      return { success: false, error: err?.message || "An error occurred." };
    }
  }

  static async pollPushStatus(challengeId: string): Promise<{ status: "PENDING" | "APPROVED" | "REJECTED" | "EXPIRED"; data?: any; error?: string }> {
    try {
      const response = await ApiService.get<{ status: "PENDING" | "APPROVED" | "REJECTED" | "EXPIRED"; data?: any }>(`/auth/push/status/${challengeId}`);
      // 400 = challenge invalid or already used; stop polling instead of waiting forever
      if (response.status === 400) {
        return { status: "EXPIRED", error: response.error };
      }
      if (response.data) {
        const payload = response.data.data || {};
        const status = payload.status;
        
        if (payload.accessToken) {
          await Session.setToken(payload.accessToken);
          await Session.setUser(payload.user);
          return { status: "APPROVED", data: payload };
        }
        return { status, data: payload };
      } 
      return { status: "PENDING", error: response.error || "Failed to check status." };
    } catch (err: any) {
      return { status: "PENDING", error: err?.message || "An error occurred." };
    }
  }

  static async simulatePushAction(challengeId: string, action: "APPROVE" | "REJECT"): Promise<{ success: boolean; error?: string }> {
    try {
      RemoteLogger.info("[AuthService] simulatePushAction called with:", { challengeId, action });
      const token = await Session.getToken() || "dummy_token_for_testing";
      const response = await ApiService.post<{ success: boolean; message?: string }>("/auth/push/action", {
        challengeId,
        action,
      }, token);

      if (response.data && response.data.success) {
        return { success: true };
      }
      return { success: false, error: response.error || response.data?.message || "Action request failed." };
    } catch (err: any) {
      return { success: false, error: err?.message || "An error occurred." };
    }
  }

  // ── Email + password, email codes, password reset ─────────────────────────
  // Same endpoints as the admin panel, but clientType "mobile": "web" is admin-only and
  // rejects normal users ("You are not an admin of any organization"). The mobile reply
  // is { accessToken, user }, the same shape push login returns.

  static async saveSession(accessToken: string, user: any): Promise<void> {
    await Session.setToken(accessToken);
    await Session.setUser(user);
  }

  // Does not save the session: the caller may need an email code first
  static async emailLogin(
    email: string,
    password: string
  ): Promise<{ success: boolean; accessToken?: string; user?: any; isEmailVerificationRequired?: boolean; error?: string }> {
    const response = await ApiService.post<any>("/auth/login", {
      email: email.trim().toLowerCase(),
      password,
      clientType: "mobile",
      deviceId: await getDeviceId(),
    });
    const payload = response.data?.data;
    if (payload?.accessToken) {
      return {
        success: true,
        accessToken: payload.accessToken,
        user: payload.user,
        isEmailVerificationRequired: payload.isEmailVerificationRequired === true,
      };
    }
    return { success: false, error: response.error || "Invalid email or password" };
  }

  // purpose "reverify": confirm the email after login; "reset": forgot password.
  // `otp` is only returned for flagged test users.
  static async requestEmailCode(
    email: string,
    purpose: "reverify" | "reset"
  ): Promise<{ success: boolean; secret?: string; otp?: string; error?: string }> {
    const response = await ApiService.post<any>(`/auth/mobile/request-otp?purpose=${purpose}`, {
      email: email.trim().toLowerCase(),
      deviceId: await getDeviceId(),
    });
    const payload = response.data?.data ?? response.data;
    if (payload?.secret) {
      return { success: true, secret: payload.secret, otp: payload.otp ? String(payload.otp) : undefined };
    }
    return { success: false, error: response.error || "Failed to send the code" };
  }

  static async verifyEmailCode(
    secret: string,
    otp: string
  ): Promise<{ success: boolean; authenticationToken?: string; error?: string }> {
    const response = await ApiService.post<any>("/auth/verify-otp", {
      secret,
      otp,
      deviceId: await getDeviceId(),
    });
    if (response.data) {
      const payload = response.data?.data ?? response.data;
      return { success: true, authenticationToken: payload?.authenticationToken };
    }
    return { success: false, error: response.error || "Invalid code" };
  }

  // Turns a verified reset code into a normal session (Bearer login, no body)
  static async loginWithCodeToken(
    authenticationToken: string
  ): Promise<{ success: boolean; accessToken?: string; user?: any; error?: string }> {
    const response = await ApiService.post<any>("/auth/login", {}, authenticationToken);
    const payload = response.data?.data;
    if (payload?.accessToken) {
      return { success: true, accessToken: payload.accessToken, user: payload.user };
    }
    return { success: false, error: response.error || "Verification failed" };
  }

  // First password needs no current password (PATCH /users/self)
  static async setPassword(password: string, accessToken: string): Promise<{ success: boolean; error?: string }> {
    const response = await ApiService.patch<any>("/users/self", { password }, accessToken);
    if (response.data) return { success: true };
    return { success: false, error: response.error || "Failed to set password" };
  }

  static async logout(): Promise<void> {
    await Session.clear();
  }

  static async getCurrentUser(): Promise<any> {
    return await Session.getUser();
  }

  static async isAuthenticated(): Promise<boolean> {
    return await Session.isLoggedIn();
  }
}
